import React, { useState } from 'react';
import { Upload, X, FileText, CheckCircle, Download, Info } from 'lucide-react';
import { DocumentAnalyzer } from '../../lib/document-analysis';
import { useAuth } from '../../contexts/AuthContext';
import DocumentReviewForm from './DocumentReviewForm';
import toast from 'react-hot-toast';

interface ProfileAutoFillProps {
  onClose: () => void;
}

export function ProfileAutoFill({ onClose }: ProfileAutoFillProps) {
  const { updateProfile } = useAuth();
  const [uploading, setUploading] = useState<boolean>(false);
  const [extractedData, setExtractedData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (!e.target.files || e.target.files.length === 0) return;
      const file = e.target.files[0];
      setUploading(true);
      setError(null);

      // Extract text from file
      const text = await DocumentAnalyzer.processFile(file);
      
      // Analyze the text and extract structured data
      const data = await DocumentAnalyzer.analyzeDocument(text);
      setExtractedData(data);
    } catch (error) {
      console.error('Error processing document:', error);
      setError(error instanceof Error ? error.message : 'Failed to process document');
      toast.error('Failed to process document');
    } finally {
      setUploading(false);
    }
  };

  const handleApplyData = async (selectedData: any) => {
    try {
      await updateProfile(selectedData);
      toast.success('Profile updated successfully');
      onClose();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    }
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/templates/capability_statement_template.rtf';
    link.download = 'BidWin_Capability_Statement_Template.rtf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="bg-bw-surface rounded-lg shadow p-6">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-lg font-semibold">Auto-fill Profile</h3>
          <p className="text-sm text-bw-muted mt-1">
            Upload a capability statement or business profile document to automatically extract information.
          </p>
        </div>
        <button onClick={onClose} className="text-bw-muted hover:text-bw-muted">
          <X className="h-6 w-6" />
        </button>
      </div>

      {!extractedData ? (
        <div className="mt-4">
          {/* Template Download Section */}
          <div className="bg-bw-primary-tint rounded-lg p-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <FileText className="h-8 w-8 text-bw-primary" />
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-medium text-bw-primary">Capability Statement Template</h4>
                <p className="mt-1 text-sm text-bw-muted">
                  For best results, download and use our template. It's designed to ensure accurate data extraction and includes all required fields.
                </p>
                <button
                  onClick={handleDownloadTemplate}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-servet-blue text-sm font-medium rounded-md text-bw-primary hover:bg-bw-primary-tint focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bw-primary"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Download Template
                </button>
              </div>
            </div>
          </div>

          <div className="relative">
            <input
              type="file"
              onChange={handleFileUpload}
              accept=".pdf,.doc,.docx,.rtf"
              className="hidden"
              id="document-upload"
              disabled={uploading}
            />
            <label
              htmlFor="document-upload"
              className={`flex flex-col items-center justify-center w-full h-64 px-4 transition bg-bw-surface border-2 border-bw-border border-dashed rounded-lg appearance-none cursor-pointer hover:border-gray-400 focus:outline-none ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {uploading ? (
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  <p className="mt-4 text-sm text-bw-muted">Processing document...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <Upload className="w-12 h-12 text-bw-muted" />
                  <p className="mt-4 text-sm text-bw-muted">Click to upload or drag and drop</p>
                  <p className="text-xs text-bw-muted">PDF, DOC, DOCX, or RTF (max. 10MB)</p>
                </div>
              )}
            </label>
          </div>
        </div>
      ) : (
        <DocumentReviewForm
          extractedData={extractedData}
          onSave={handleApplyData}
          onCancel={() => setExtractedData(null)}
        />
      )}
    </div>
  );
}